import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrentOrgId } from '@/hooks/auth';
import { queryConfigs } from '@/lib/queryClient';
import { useMemo } from 'react';
import { groupFieldsByGroup, UNGROUPED_GROUP_ID } from '@/services/fieldRegistry';

/**
 * O grupo "Outros" é virtual (id `__ungrouped__`) e não existe em `field_groups`.
 * Gravá-lo em `organization_custom_fields.group_id` (uuid) estoura 22P02 — que o
 * insert de criação mascarava caindo no fallback "só colunas base", perdendo
 * `is_system` e as flags de visibilidade. Normaliza para NULL, que é o que o
 * bucket "Outros" significa.
 */
function normalizeGroupId(groupId: string | null | undefined): string | null | undefined {
  return groupId === UNGROUPED_GROUP_ID ? null : groupId;
}
import {
  getDefaultSystemFields,
  getDefaultSystemGroup,
  type EntityType,
  type FieldDefinition,
  type FieldGroup,
  type FieldGroupWithFields,
  type CreateFieldData,
  type UpdateFieldData,
  type CreateFieldGroupData,
  type UpdateFieldGroupData,
  type FieldVisibilityUpdate,
} from '@/types/fieldRegistry';

// ============================================================================
// Hook principal: busca campos + grupos unificados
// ============================================================================

export function useFieldRegistry(entityType: EntityType) {
  const { orgId } = useCurrentOrgId();

  const fieldsQuery = useQuery({
    ...queryConfigs.structural,
    queryKey: ['field-registry', 'fields', orgId, entityType],
    queryFn: async (): Promise<FieldDefinition[]> => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('organization_custom_fields')
        .select('*')
        .eq('org_id', orgId)
        .eq('page_type', entityType)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching field registry:', error);
        throw error;
      }

      return (data || []) as FieldDefinition[];
    },
    enabled: !!orgId,
  });

  const groupsQuery = useQuery({
    ...queryConfigs.structural,
    queryKey: ['field-registry', 'groups', orgId, entityType],
    queryFn: async (): Promise<FieldGroup[]> => {
      if (!orgId) return [];

      try {
        const { data, error } = await supabase
          .from('field_groups')
          .select('*')
          .eq('org_id', orgId)
          .eq('entity_type', entityType)
          .order('display_order', { ascending: true });

        if (error) {
          // Tabela pode não existir ainda (migração não aplicada)
          console.warn('field_groups query failed (migration may not be applied):', error.message);
          return [];
        }

        return (data || []) as FieldGroup[];
      } catch (e) {
        console.warn('field_groups query exception:', e);
        return [];
      }
    },
    enabled: !!orgId,
  });

  const dbFields = fieldsQuery.data || [];
  const dbGroups = groupsQuery.data || [];

  // Fallback: se o banco não retornou campos sistema, mesclar com os defaults do frontend
  const { fields, groups } = useMemo(() => {
    const hasSystemFields = dbFields.some((f) => f.is_system);
    const hasSystemGroup = dbGroups.some((g) => g.is_system);

    if (hasSystemFields && hasSystemGroup) {
      // Migração já rodou — usar dados do banco normalmente
      return { fields: dbFields, groups: dbGroups };
    }

    // Montar fallback
    const effectiveOrgId = orgId || '';

    // Adicionar grupo default se necessário
    const fallbackGroups = hasSystemGroup
      ? dbGroups
      : [getDefaultSystemGroup(entityType, effectiveOrgId), ...dbGroups];

    // Adicionar campos default se necessário
    const fallbackFields = hasSystemFields
      ? dbFields
      : [...getDefaultSystemFields(entityType, effectiveOrgId), ...dbFields];

    return { fields: fallbackFields, groups: fallbackGroups };
  }, [dbFields, dbGroups, entityType, orgId]);

  const groupedFields: FieldGroupWithFields[] = groupFieldsByGroup(fields, groups);

  return {
    fields,
    groups,
    groupedFields,
    isLoading: fieldsQuery.isLoading || groupsQuery.isLoading,
    error: fieldsQuery.error || groupsQuery.error,
  };
}

// ============================================================================
// Hook para todos os campos de todas as entidades (admin)
// ============================================================================

function useAllFieldRegistry() {
  const { orgId } = useCurrentOrgId();

  const fieldsQuery = useQuery({
    ...queryConfigs.structural,
    queryKey: ['field-registry', 'fields', orgId, 'all'],
    queryFn: async (): Promise<FieldDefinition[]> => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('organization_custom_fields')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .order('page_type', { ascending: true })
        .order('display_order', { ascending: true });

      if (error) throw error;
      return (data || []) as FieldDefinition[];
    },
    enabled: !!orgId,
  });

  const groupsQuery = useQuery({
    ...queryConfigs.structural,
    queryKey: ['field-registry', 'groups', orgId, 'all'],
    queryFn: async (): Promise<FieldGroup[]> => {
      if (!orgId) return [];

      try {
        const { data, error } = await supabase
          .from('field_groups')
          .select('*')
          .eq('org_id', orgId)
          .order('entity_type', { ascending: true })
          .order('display_order', { ascending: true });

        if (error) {
          console.warn('field_groups (all) query failed:', error.message);
          return [];
        }
        return (data || []) as FieldGroup[];
      } catch (e) {
        console.warn('field_groups (all) query exception:', e);
        return [];
      }
    },
    enabled: !!orgId,
  });

  return {
    fields: fieldsQuery.data || [],
    groups: groupsQuery.data || [],
    isLoading: fieldsQuery.isLoading || groupsQuery.isLoading,
  };
}

// ============================================================================
// CRUD de campos
// ============================================================================

export function useCreateField() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (data: CreateFieldData): Promise<FieldDefinition> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      // Montar objeto base (colunas que sempre existem na tabela)
      const baseInsert: Record<string, any> = {
        org_id: orgId,
        field_name: data.field_name,
        field_type: data.field_type,
        field_label: data.field_label,
        page_type: data.page_type,
        display_order: data.display_order ?? 0,
        is_required: data.is_required ?? false,
      };
      if (data.field_placeholder) baseInsert.field_placeholder = data.field_placeholder;
      if (data.field_options) baseInsert.field_options = data.field_options;
      // Na base (e não no registry): o fallback sem colunas do registry não pode perder a fórmula,
      // senão o banco recusa o campo calculado com um erro sem pista do motivo.
      if (data.formula) baseInsert.formula = data.formula;

      // Colunas do registry (podem não existir se migração não rodou)
      const registryColumns: Record<string, any> = {
        is_system: false,
      };
      // `null` é explícito (grupo "Outros") e precisa sobrescrever o valor antigo
      // quando reaproveitamos um registro soft-deleted na reativação.
      if (data.group_id !== undefined) registryColumns.group_id = normalizeGroupId(data.group_id);
      if (data.is_visible_in_add_form !== undefined) registryColumns.is_visible_in_add_form = data.is_visible_in_add_form;
      if (data.is_visible_in_detail !== undefined) registryColumns.is_visible_in_detail = data.is_visible_in_detail;
      if (data.is_visible_in_list !== undefined) registryColumns.is_visible_in_list = data.is_visible_in_list;
      if (data.is_filterable !== undefined) registryColumns.is_filterable = data.is_filterable;
      if (data.is_sortable !== undefined) registryColumns.is_sortable = data.is_sortable;

      // Detecta violação da constraint UNIQUE(org_id, field_name, page_type).
      // Como a exclusão de campos é "soft" (is_active = false), a linha permanece
      // na tabela e ocupa o slot único — recriar um campo com o mesmo nome colide
      // com o registro inativo (invisível na UI), gerando "duplicate key value".
      const isUniqueViolation = (err: any) =>
        err?.code === '23505' || /duplicate key|unique constraint/i.test(err?.message || '');

      // Tentar inserir com todas as colunas (registry + base)
      const fullInsert = { ...baseInsert, ...registryColumns };
      let { data: result, error } = await supabase
        .from('organization_custom_fields')
        .insert([fullInsert] as never)
        .select()
        .single();

      // Se falhou por colunas inexistentes (migração não aplicada), tentar só base
      if (error && !isUniqueViolation(error)) {
        console.warn('[useCreateField] Insert com colunas do registry falhou, tentando somente colunas base:', error.message);
        const fallback = await supabase
          .from('organization_custom_fields')
          .insert([baseInsert] as never)
          .select()
          .single();
        result = fallback.data;
        error = fallback.error;
      }

      // Colisão com a constraint única: localizar o registro existente.
      // Se estiver inativo (soft-deleted), reativar reaproveitando a mesma linha
      // — os valores em custom_data são chaveados por field_name, então a
      // continuidade dos dados é preservada. Se estiver ativo, é duplicata real.
      if (error && isUniqueViolation(error)) {
        const { data: existing } = await supabase
          .from('organization_custom_fields')
          .select('id, is_active')
          .eq('org_id', orgId)
          .eq('field_name', data.field_name)
          .eq('page_type', data.page_type)
          .maybeSingle();

        if (existing && (existing as any).is_active) {
          throw new Error('Já existe um campo com esse identificador para esta entidade.');
        }

        if (existing) {
          const reactivateFull = { ...fullInsert, is_active: true };
          const reactivated = await supabase
            .from('organization_custom_fields')
            .update(reactivateFull as never)
            .eq('id', (existing as any).id)
            .select()
            .single();

          if (reactivated.error) {
            // Fallback: somente colunas base (migração do registry não aplicada)
            const reactivatedBase = await supabase
              .from('organization_custom_fields')
              .update({ ...baseInsert, is_active: true } as never)
              .eq('id', (existing as any).id)
              .select()
              .single();
            if (reactivatedBase.error) throw reactivatedBase.error;
            return reactivatedBase.data as FieldDefinition;
          }
          return reactivated.data as FieldDefinition;
        }

        throw error;
      }

      if (error) throw error;
      return result as FieldDefinition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      queryClient.invalidateQueries({ queryKey: ['organization-custom-fields'] });
      toast({
        title: "Campo criado",
        description: "O campo foi adicionado com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao criar campo",
        description: error.message || "Não foi possível criar o campo.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateField() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (data: UpdateFieldData): Promise<FieldDefinition> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      const { id, ...updateData } = data;

      // Separar colunas base das colunas do registry
      const baseKeys = ['field_name', 'field_type', 'field_label', 'field_placeholder', 'field_options', 'is_required', 'display_order', 'page_type', 'formula'];
      const baseUpdate: Record<string, any> = {};
      const fullUpdate: Record<string, any> = {};

      for (const [key, value] of Object.entries(updateData)) {
        fullUpdate[key] = key === 'group_id' ? normalizeGroupId(value as string | null) : value;
        if (baseKeys.includes(key)) {
          baseUpdate[key] = value;
        }
      }

      // Tentar update com todas as colunas
      const { data: result, error } = await supabase
        .from('organization_custom_fields')
        .update(fullUpdate)
        .eq('id', id)
        .eq('org_id', orgId)
        .select()
        .single();

      // Fallback: somente colunas base
      if (error && Object.keys(baseUpdate).length > 0) {
        console.warn('[useUpdateField] Update com colunas do registry falhou, tentando somente base:', error.message);
        const { data: fallbackResult, error: fallbackError } = await supabase
          .from('organization_custom_fields')
          .update(baseUpdate)
          .eq('id', id)
          .eq('org_id', orgId)
          .select()
          .single();

        if (fallbackError) throw fallbackError;
        return fallbackResult as FieldDefinition;
      }

      if (error) throw error;
      return result as FieldDefinition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      queryClient.invalidateQueries({ queryKey: ['organization-custom-fields'] });
      toast({
        title: "Campo atualizado",
        description: "As alterações foram salvas.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar campo",
        description: error.message || "Não foi possível atualizar o campo.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteField() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (fieldId: string): Promise<void> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      // Verificar se não é campo sistema
      const { data: field } = await supabase
        .from('organization_custom_fields')
        .select('is_system')
        .eq('id', fieldId)
        .single();

      if (field?.is_system) {
        throw new Error('Campos do sistema não podem ser excluídos');
      }

      const { error } = await supabase
        .from('organization_custom_fields')
        .update({ is_active: false })
        .eq('id', fieldId)
        .eq('org_id', orgId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      queryClient.invalidateQueries({ queryKey: ['organization-custom-fields'] });
      toast({
        title: "Campo excluído",
        description: "O campo foi removido com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir campo",
        description: error.message || "Não foi possível excluir o campo.",
        variant: "destructive",
      });
    },
  });
}

export function useReorderFields() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (fields: { id: string; display_order: number }[]): Promise<void> => {
      const updates = fields.map((field) =>
        supabase
          .from('organization_custom_fields')
          .update({ display_order: field.display_order })
          .eq('id', field.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw new Error('Erro ao reordenar campos');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      queryClient.invalidateQueries({ queryKey: ['organization-custom-fields'] });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao reordenar",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// ============================================================================
// Visibilidade em lote
// ============================================================================

export function useUpdateFieldVisibility() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (updates: FieldVisibilityUpdate[]): Promise<void> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      const promises = updates.map(({ id, ...flags }) =>
        supabase
          .from('organization_custom_fields')
          .update(flags)
          .eq('id', id)
          .eq('org_id', orgId)
      );

      const results = await Promise.all(promises);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw new Error('Erro ao atualizar visibilidade');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      queryClient.invalidateQueries({ queryKey: ['organization-custom-fields'] });
      toast({
        title: "Visibilidade atualizada",
        description: "As configurações de visibilidade foram salvas.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar visibilidade",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// ============================================================================
// CRUD de grupos
// ============================================================================

export function useCreateFieldGroup() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (data: CreateFieldGroupData): Promise<FieldGroup> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      const { data: result, error } = await supabase
        .from('field_groups')
        .insert([{ ...data, org_id: orgId }])
        .select()
        .single();

      if (error) throw error;
      return result as FieldGroup;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry', 'groups'] });
      toast({
        title: "Grupo criado",
        description: "O grupo de campos foi criado com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao criar grupo",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

function useUpdateFieldGroup() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (data: UpdateFieldGroupData): Promise<FieldGroup> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      const { id, ...updateData } = data;

      const { data: result, error } = await supabase
        .from('field_groups')
        .update(updateData)
        .eq('id', id)
        .eq('org_id', orgId)
        .select()
        .single();

      if (error) throw error;
      return result as FieldGroup;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry', 'groups'] });
      toast({
        title: "Grupo atualizado",
        description: "As alterações do grupo foram salvas.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar grupo",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteFieldGroup() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (groupId: string): Promise<void> => {
      if (!orgId) throw new Error('ID da organização não encontrado');

      // Verificar se não é grupo sistema
      const { data: group } = await supabase
        .from('field_groups')
        .select('is_system')
        .eq('id', groupId)
        .single();

      if (group?.is_system) {
        throw new Error('Grupos do sistema não podem ser excluídos');
      }

      // Campos do grupo serão desassociados (ON DELETE SET NULL)
      const { error } = await supabase
        .from('field_groups')
        .delete()
        .eq('id', groupId)
        .eq('org_id', orgId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry'] });
      toast({
        title: "Grupo excluído",
        description: "O grupo foi removido. Os campos foram desassociados.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao excluir grupo",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

function useReorderFieldGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groups: { id: string; display_order: number }[]): Promise<void> => {
      const updates = groups.map((group) =>
        supabase
          .from('field_groups')
          .update({ display_order: group.display_order })
          .eq('id', group.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) throw new Error('Erro ao reordenar grupos');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-registry', 'groups'] });
    },
  });
}
